import axios from 'axios';

/**
 * Builds the string for an image's srcSet attribute given a set of widths.
 *
 * @param   {string} src     The original image src attribute.
 * @param   {array}  widths  Array of image widths in pixels.
 * @returns {string}         The corresponding srcSet attribute. Returns undefined if the URL
 *                           is not a valid filestack URl.
 */
export function buildImageSrcSet(src, widths) {

	const filestackData = parseFilestackURL(src);

	if (filestackData && filestackData.responsive) {

		const { policy, signature, handle, url } = filestackData;

		let srcSet = '';

		widths.forEach((width, idx) => {

			const resizedSrc = `https://cdn.filestackcontent.com/resize=width:${width}/rotate=deg:exif/security=policy:${policy},signature:${signature}/fallback=handle:${handle}/${url}`;

			srcSet += `${resizedSrc} ${width}w`;

			// Comma-separate source values
			if (idx !== widths.length - 1) {

				srcSet += ', ';

			}

		});

		return srcSet;

	}

	return undefined;

}

/**
 * Builds the string for the 'sizes' attributes for responsive images,
 * assuming possible width values of [25%, 50%, 75%, 100%].
 *
 * @param {string} width  The image size as a percentage.
 * @returns {string}  The sizes attribute.
 */
export function buildImageSizes(width) {

	// Parse percentage string
	const percentage = width && parseInt(width.substring(0, width.length - 1), 10) / 100;

	const imageSizes = {
		small: 100 * (percentage || 1),
		medium: 90 * (percentage || 1),
		large: 75 * (percentage || 1),
	};

	// eslint-disable-next-line max-len
	return `(max-width: 576px) ${imageSizes.small}vw, (max-width: 768px) ${imageSizes.medium}vw, ${imageSizes.large}vw`;

}

/**
 * Builds the string for the 'sizes' attributes for responsive images,
 * assuming a grid of possible width values of [1, 2, 3, 4, 6, 8, 12].
 *
 * @param {number} width  The slide width as a boostrap column value.
 * @returns {string}  The sizes attribute.
 */
export function buildImageSizesForContentGrid(width) {

	const imageSizes = {
		small: 100 * (width / 12),
		medium: 90 * (width / 12),
		large: 75 * (width / 12),
	};

	// eslint-disable-next-line max-len
	return `(max-width: 576px) ${imageSizes.small}vw, (max-width: 768px) ${imageSizes.medium}vw, ${imageSizes.large}vw`;

}

/**
 * Determines whether or not the image should be responsive.
 *
 * @param {string}  src  The image source.
 * @returns {boolean}  False if image type is a gif or not a filestack image, true otherwise.
 */
export function isResponsive(src) {

	return new Promise((resolve, reject) => {

		const filestackData = parseFilestackURL(src);

		if (filestackData) {

			const { handle, policy, signature } = filestackData;

			const metaDataUrl = `https://www.filestackapi.com/api/file/${handle}/metadata?policy=${policy}&signature=${signature}`;

			// Fetch image metadata
			return axios.get(metaDataUrl).then((response) => {

				let result = false;

				// Check file type
				if (response && response.data) {

					const { mimetype, filename } = response.data;

					result = (mimetype && mimetype.indexOf('gif') === -1) &&
						(filename && filename.indexOf('.gif') === -1);

				}

				return resolve(result);

			}).catch((error) => {

				return reject(error);

			});

		}

		return resolve(false);

	});

}

/**
 * Parses a filestack URL and returns set of embedded metadata.
 *
 * @param {string} imageSrc  The filestack URL.
 * @returns {Object}  The set of filestack metadata or undefined if imageSrc
 *	is not a filestack URL.
 */
export function parseFilestackURL(imageSrc) {

	if (imageSrc && imageSrc.indexOf('cdn.filestackcontent.com') >= 0) {

		let responsive = false;

		let imageUrl = imageSrc.split('&responsive=');

		if (imageSrc.length !== imageUrl[0].length) {

			// Remove responsive flag
			responsive = imageUrl.pop() === 'true';

		}

		imageUrl = imageUrl[0].split('&signature=');

		// Remove the filestack security signature
		const signature = imageUrl.pop();

		imageUrl = imageUrl[0].split('?policy=');

		// Remove the filestack security policy
		const policy = imageUrl.pop();

		// Get the filestack image url
		const url = imageUrl[0];

		const handle = url.split('https://cdn.filestackcontent.com/').pop();

		return {
			policy,
			signature,
			handle,
			url,
			responsive,
		};

	}

	return undefined;

}

/**
 * Generates a resized url for an image (if the image is from filestack).
 *
 * @param   {string} image      The filestack image url.
 * @param   {string} direction  The direction to resize, i.e. "width" or "height"
 * @param   {number} size       The pixel size to resize based on the direction.
 * @returns {string}            The resized image url.
 */
export function resizeImage(image, direction, size) {

	try {

		const filestack = parseFilestackURL(image);

		// Check if this image is a filestack image
		// If so, resize it!
		if (filestack) {

			const { policy, signature, url } = filestack;

			// Get the device pixel ratio to upscale for retina displays
			// Falls back to 1 for legacy browsers (mainly just IE 10) or
			// when a user has zoomed less than 100%
			const devicePixelRatio = window.devicePixelRatio >= 1 ? window.devicePixelRatio : 1;
			const responsiveSize = size && Math.ceil(size * devicePixelRatio);

			// This is needed to handle rotation metadata added by phones that a desktop
			// browser might ignore
			const rotateTransform = 'rotate=deg:exif/';
			const resizeTransform = (responsiveSize && direction)
				? `resize=${direction}:${responsiveSize}/` : '';
			const security = `security=policy:${policy},signature:${signature}/`;

			const resizedURL = `https://cdn.filestackcontent.com/${resizeTransform}${rotateTransform}${security}${url}`;

			return resizedURL;

		}

	} catch (error) {
		console.error(`Error resizing the filestack image=${image}`);
		console.error(error);
	}

	return image;

}
